import { useMemo, useState } from "react";
import { Users, Repeat, ArrowRight } from "lucide-react";
import Reveal from "../landing/Reveal";
import { Slider } from "@/components/ui/slider";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCalc } from "@/state/CalcContext";

const formatNumber = (n, lang) =>
  new Intl.NumberFormat(lang === "es" ? "es-ES" : "it-IT", { maximumFractionDigits: 0 }).format(n);

export default function CalcSection() {
  const { t, lang } = useLanguage();
  const { setCalc } = useCalc();
  const [population, setPopulation] = useState(100000);
  const [interventions, setInterventions] = useState(25);

  const result = useMemo(() => {
    const scale = (population / 100000) * (interventions / 25);
    return {
      reactiveLow: Math.round(21600 * scale),
      reactiveHigh: Math.round(35300 * scale),
      savings: Math.round(69600 * scale),
      km: Math.round(104500 * scale),
      co2: (27 * scale).toFixed(1),
    };
  }, [population, interventions]);

  const track = (pop, intv) => {
    const scale = (pop / 100000) * (intv / 25);
    setCalc({
      population: pop,
      interventions: intv,
      reactiveLow: Math.round(21600 * scale),
      reactiveHigh: Math.round(35300 * scale),
      savings: Math.round(69600 * scale),
      km: Math.round(104500 * scale),
      co2: (27 * scale).toFixed(1),
    });
  };

  return (
    <section id="calcolatore" className="bg-cream py-20 md:py-28" data-testid="calculator-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <Reveal>
          <p className="kicker" data-testid="calculator-kicker">{t.calculator.kicker}</p>
          <h2 className="mt-5 font-display text-3xl font-bold leading-tight text-forest-800 md:text-4xl lg:text-5xl">
            {t.calculator.title}
          </h2>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-forest-800/80">{t.calculator.description}</p>
        </Reveal>

        <div className="mt-12 grid items-start gap-8 lg:grid-cols-2">
          <Reveal>
            <div className="border border-forest-800/30 bg-white p-8" data-testid="calculator-controls">
              <div className="space-y-10">
                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm font-semibold text-forest-800">
                      <Users size={16} className="text-ember" /> {t.calculator.population}
                    </label>
                    <span className="font-mono text-lg font-bold text-forest-800" data-testid="calculator-population-value">
                      {formatNumber(population, lang)}
                    </span>
                  </div>
                  <Slider
                    value={[population]}
                    onValueChange={([v]) => { setPopulation(v); track(v, interventions); }}
                    min={5000}
                    max={1000000}
                    step={5000}
                    data-testid="calculator-population-slider"
                  />
                </div>

                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm font-semibold text-forest-800">
                      <Repeat size={16} className="text-ember" /> {t.calculator.interventions}
                    </label>
                    <span className="font-mono text-lg font-bold text-forest-800" data-testid="calculator-interventions-value">
                      {interventions}
                    </span>
                  </div>
                  <Slider
                    value={[interventions]}
                    onValueChange={([v]) => { setInterventions(v); track(population, v); }}
                    min={1}
                    max={60}
                    step={1}
                    data-testid="calculator-interventions-slider"
                  />
                </div>
              </div>

              <p className="rule mt-10 pt-5 text-xs italic leading-relaxed text-forest-800/60" data-testid="calculator-disclaimer">
                {t.calculator.disclaimer}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="bg-forest-900 p-8" data-testid="calculator-results">
              <div className="grid grid-cols-2 gap-px bg-cream/15">
                <div className="bg-forest-900 px-5 py-6">
                  <p className="font-display text-2xl font-bold text-cream md:text-3xl" data-testid="calculator-reactive-result">
                    {formatNumber(result.reactiveLow, lang)} – {formatNumber(result.reactiveHigh, lang)} €
                  </p>
                  <p className="mt-1 text-xs text-cream/60">{t.calculator.reactive}</p>
                </div>
                <div className="bg-forest-900 px-5 py-6">
                  <p className="font-display text-2xl font-bold text-mint md:text-3xl" data-testid="calculator-annual-result">
                    ~{formatNumber(result.savings, lang)} €
                  </p>
                  <p className="mt-1 text-xs text-cream/60">{t.calculator.savings}</p>
                </div>
                <div className="bg-forest-900 px-5 py-6">
                  <p className="font-display text-xl font-bold text-cream md:text-2xl" data-testid="calculator-km-result">
                    {formatNumber(result.km, lang)} km
                  </p>
                  <p className="mt-1 text-xs text-cream/60">{t.calculator.km}</p>
                </div>
                <div className="bg-forest-900 px-5 py-6">
                  <p className="font-display text-xl font-bold text-cream md:text-2xl" data-testid="calculator-co2-result">
                    {result.co2} t
                  </p>
                  <p className="mt-1 text-xs text-cream/60">{t.calculator.co2}</p>
                </div>
              </div>

              <a
                href="#contacto"
                data-testid="calculator-cta"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 bg-ember px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-orange-600"
              >
                {t.calculator.cta} <ArrowRight size={17} />
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
